import { useCallback, useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { api } from '../lib/api'
import { RESPONSE_PLAN_PREVIEW } from '../data/response-plan-preview'
import { filterTasks, PLAN_NOTE_MAX_CHARS, planGroups, type TaskOwnerFilter, type TaskStatusFilter } from '../lib/response-plan'
import type { PlanChip, PlanNote, PlanSection, PlanTask, ResponsePlanView, ThreadReply } from '../types'
import { Alert, Button, EmptyState, Field, StatusBadge, useToast, type StatusTone } from './ui'
import { ChatAdd, Check, ChevronDown, Inbox, SpinnerGap } from './ui/icons'
import { ExternalLink, formatThreadTime, ThreadReplies } from './ThreadReplies'

const CHIP_TONE: Record<PlanChip, StatusTone> = { done: 'success', prog: 'pending', beta: 'info', plan: 'neutral', due: 'neutral' }
const MILESTONE_LABEL = { done: 'Done', next: 'Next', later: 'Coming' } as const
const MILESTONE_TONE: Record<'done' | 'next' | 'later', StatusTone> = { done: 'success', next: 'pending', later: 'neutral' }
const PREVIEW_AUTHOR = 'Test Tester'

/**
 * The Response Plan tab (Rod, 2026-11-04): native portal screens instead of
 * one long page in a frame. A section nav (Timeline, Tasks, What We're
 * Shipping, Feedback, Also on Our List) shows one part at a time.
 *
 * - Anyone signed in on either side can mark any task done or open it again;
 *   the task shows who did it and when.
 * - A verified customer adds a note on any part. Each note becomes a Linear
 *   Triage issue and a Slack alert, and both sides reply in its thread.
 * - When the Prentus Assistant is on, it answers a new note in the thread.
 *   The page waits for that answer in its own request.
 *
 * `preview` (local fixtures only) uses labeled test data and makes no requests.
 */
export function ResponsePlanTab({ publicId, preview = false }: { publicId: string; preview?: boolean }) {
  const toast = useToast()
  const [plan, setPlan] = useState<ResponsePlanView | null>(preview ? RESPONSE_PLAN_PREVIEW : null)
  const [state, setState] = useState<'loading' | 'ready' | 'error'>(preview ? 'ready' : 'loading')
  const [group, setGroup] = useState('timeline')
  const [pendingNotes, setPendingNotes] = useState<Set<string>>(new Set())

  const load = useCallback(async () => {
    if (preview) return
    try {
      setPlan(await api.loadResponsePlan(publicId))
      setState('ready')
    } catch {
      setState((current) => (current === 'ready' ? current : 'error'))
    }
  }, [preview, publicId])
  useEffect(() => { void load() }, [load])

  const groups = useMemo(() => planGroups(plan?.sections || []), [plan?.sections])
  const active = groups.find((entry) => entry.id === group) || groups[0]

  /** Waits for the Assistant's answer in this thread, then reloads the plan. */
  const awaitAssistant = useCallback(async (noteId: string, runId: string | null) => {
    if (!runId) return
    setPendingNotes((current) => new Set(current).add(noteId))
    try {
      await api.runAssistant(publicId, runId)
    } catch {
      toast.info('The Answer Is Still Coming', { description: 'Refresh the page in a minute to see it.' })
    } finally {
      setPendingNotes((current) => { const next = new Set(current); next.delete(noteId); return next })
      await load()
    }
  }, [load, publicId, toast])

  async function sendNote(section: PlanSection, body: string): Promise<boolean> {
    if (!plan) return false
    if (preview) {
      const note: PlanNote = { id: `preview-${Date.now()}`, sectionKey: section.key, sectionTitle: section.title, body, authorName: PREVIEW_AUTHOR, createdAt: new Date().toISOString(), replies: [] }
      setPlan({ ...plan, notes: [...plan.notes, note] })
      toast.success('Note Sent')
      return true
    }
    try {
      const { note, assistantRunId } = await api.sendPlanNote(publicId, section.key, body)
      setPlan((current) => current && { ...current, notes: [...current.notes, note] })
      toast.success('Note Sent', { description: 'The Prentus team has it now.' })
      void awaitAssistant(note.id, assistantRunId)
      return true
    } catch (cause) {
      toast.error('Note Not Sent', { description: cause instanceof Error ? cause.message : undefined })
      return false
    }
  }

  async function reply(note: PlanNote, body: string): Promise<boolean> {
    if (!plan) return false
    if (preview) {
      const added: ThreadReply = { id: `preview-${Date.now()}`, body, authorName: PREVIEW_AUTHOR, authorSide: plan.viewer.side, createdAt: new Date().toISOString(), corrected: false, sources: [] }
      setPlan({ ...plan, notes: plan.notes.map((entry) => entry.id === note.id ? { ...entry, replies: [...entry.replies, added] } : entry) })
      return true
    }
    try {
      const { reply: added, assistantRunId } = await api.sendReply(publicId, { type: 'plan-note', id: note.id }, body)
      setPlan((current) => current && { ...current, notes: current.notes.map((entry) => entry.id === note.id ? { ...entry, replies: [...entry.replies, added] } : entry) })
      toast.success('Reply Sent')
      void awaitAssistant(note.id, assistantRunId)
      return true
    } catch (cause) {
      toast.error('Reply Not Sent', { description: cause instanceof Error ? cause.message : undefined })
      return false
    }
  }

  async function moderate(replyId: string, change: { body?: string; hidden?: boolean }): Promise<boolean> {
    if (preview) return true
    try {
      const { reply: updated } = await api.moderateReply(publicId, replyId, change)
      setPlan((current) => current && { ...current, notes: current.notes.map((entry) => ({ ...entry, replies: entry.replies.map((item) => item.id === replyId ? updated : item) })) })
      toast.success(change.body !== undefined ? 'Answer Corrected' : change.hidden ? 'Answer Hidden' : 'Answer Shown')
      return true
    } catch (cause) {
      toast.error('Answer Not Changed', { description: cause instanceof Error ? cause.message : undefined })
      return false
    }
  }

  async function toggleTask(task: PlanTask): Promise<void> {
    if (!plan) return
    const done = !task.done
    if (preview) {
      setPlan({ ...plan, tasks: plan.tasks.map((entry) => entry.id === task.id ? { ...entry, done, doneBy: done ? PREVIEW_AUTHOR : null, doneBySide: done ? 'customer' : null, doneAt: done ? new Date().toISOString() : null } : entry) })
      return
    }
    try {
      const { task: updated } = await api.setPlanTaskDone(publicId, task.id, done)
      setPlan((current) => current && { ...current, tasks: current.tasks.map((entry) => entry.id === updated.id ? updated : entry) })
      toast.success(done ? 'Task Marked Done' : 'Task Opened Again')
    } catch (cause) {
      toast.error('Task Not Saved', { description: cause instanceof Error ? cause.message : undefined })
      await load()
    }
  }

  if (state === 'loading') return <section className="standard-tab plan-tab"><div className="loading-lockup" role="status"><SpinnerGap size={18} className="spin" aria-hidden="true" />Loading the response plan</div></section>
  if (state === 'error' || !plan) {
    return (
      <section className="standard-tab plan-tab">
        <Alert tone="danger" title="The Response Plan Did Not Load" description="Check your connection and try again. If it still does not load, contact your Prentus team." action={<Button type="button" variant="secondary" compact onClick={() => { setState('loading'); void load() }}>Try Again</Button>} />
      </section>
    )
  }

  const notesFor = (key: string) => plan.notes.filter((note) => note.sectionKey === key)
  const thread = { plan, pendingNotes, onNote: sendNote, onReply: reply, onModerate: moderate }

  return (
    <section className="standard-tab plan-tab">
      <header className="plan-band">
        <p className="plan-band-kicker">Response Plan</p>
        <h2>{plan.title}</h2>
        <p className="plan-band-lede">{plan.lede}{plan.goal && <> The goal right now is <strong>{plan.goal}</strong>.</>}</p>
        <p className="plan-band-meta">Updated {plan.updatedLabel}. We refresh this plan every weekday.</p>
      </header>
      {preview && <Alert tone="warning" title="Preview With Test Data" description="This is a review page, not a customer workspace. Every task, note, person and answer here is test data." />}
      {!preview && plan.viewer.side === 'prentus' && <Alert tone="info" title="Prentus View" description="You can mark tasks done and reply in every thread. Only a signed-in customer adds a new note. You can correct or hide a Prentus Assistant answer." />}

      <PlanNav groups={groups.map((entry) => ({ id: entry.id, label: entry.label, notes: entry.sections.reduce((total, section) => total + notesFor(section.key).length, 0) }))} active={active?.id || ''} onSelect={setGroup} />

      {active && (
        <div className="plan-panel" role="tabpanel" id={`plan-panel-${active.id}`} aria-labelledby={`plan-tab-${active.id}`}>
          {active.id === 'shipping' && plan.shippingIntro && <p className="plan-intro">{plan.shippingIntro}</p>}
          <div className={active.id === 'shipping' ? 'plan-cards' : 'plan-sections'}>
            {active.sections.map((section) => (
              <article key={section.key} className={`plan-section plan-section--${section.kind}`} aria-labelledby={`plan-section-${section.key}`}>
                <h3 id={`plan-section-${section.key}`}>{section.title}</h3>
                {section.intro && <p className="plan-intro">{section.intro}</p>}
                <SectionBody section={section} plan={plan} onToggleTask={toggleTask} />
                <SectionNotes section={section} notes={notesFor(section.key)} {...thread} />
              </article>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}

function PlanNav({ groups, active, onSelect }: { groups: Array<{ id: string; label: string; notes: number }>; active: string; onSelect: (id: string) => void }) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({})
  function onKey(event: KeyboardEvent<HTMLDivElement>) {
    const index = groups.findIndex((entry) => entry.id === active)
    const next = event.key === 'ArrowRight' ? index + 1 : event.key === 'ArrowLeft' ? index - 1 : event.key === 'Home' ? 0 : event.key === 'End' ? groups.length - 1 : null
    if (next === null || index < 0) return
    event.preventDefault()
    const target = groups[(next + groups.length) % groups.length]
    onSelect(target.id)
    refs.current[target.id]?.focus()
  }
  return (
    <div className="plan-nav" role="tablist" aria-label="Response Plan sections" onKeyDown={onKey}>
      {groups.map((entry) => (
        <button
          key={entry.id}
          ref={(node) => { refs.current[entry.id] = node }}
          type="button"
          role="tab"
          id={`plan-tab-${entry.id}`}
          aria-selected={entry.id === active}
          aria-controls={`plan-panel-${entry.id}`}
          tabIndex={entry.id === active ? 0 : -1}
          className={entry.id === active ? 'is-active' : ''}
          onClick={() => onSelect(entry.id)}
        >
          {entry.label}
          {entry.notes > 0 && <span className="plan-nav-count" aria-label={`${entry.notes} ${entry.notes === 1 ? 'note' : 'notes'}`}>{entry.notes}</span>}
        </button>
      ))}
    </div>
  )
}

function SectionBody({ section, plan, onToggleTask }: { section: PlanSection; plan: ResponsePlanView; onToggleTask: (task: PlanTask) => Promise<void> }) {
  switch (section.kind) {
    case 'timeline':
      return (
        <>
          {section.stats.length > 0 && (
            <ul className="plan-stats">
              {section.stats.map((stat) => <li key={stat.value + stat.label}><strong>{stat.value}</strong><span>{stat.label}</span></li>)}
            </ul>
          )}
          {section.milestones.length > 0 && (
            <ol className="plan-milestones">
              {section.milestones.map((milestone, index) => (
                <li key={`${milestone.title}-${index}`} className={`plan-milestone plan-milestone--${milestone.state}`}>
                  <div className="plan-milestone-head"><span className="plan-milestone-date">{milestone.date}</span><StatusBadge tone={MILESTONE_TONE[milestone.state]}>{MILESTONE_LABEL[milestone.state]}</StatusBadge></div>
                  <h4>{milestone.title}</h4>
                  {milestone.text && <p>{milestone.text}</p>}
                </li>
              ))}
            </ol>
          )}
          {section.fixed.length > 0 && (
            <div className="plan-fixed">
              <h4>{section.fixedTitle}</h4>
              <ul className="plan-list">
                {section.fixed.map((entry) => <li key={entry.text}><span>{entry.text}</span><StatusBadge tone="success">{entry.when ? `Fixed ${entry.when}` : 'Fixed'}</StatusBadge></li>)}
              </ul>
            </div>
          )}
        </>
      )
    case 'tasks':
      return <TaskList plan={plan} link={section.link} onToggle={onToggleTask} />
    case 'card':
      return (
        <>
          <div className="plan-card-status">
            <p className="plan-card-what">{section.what}</p>
            <div>{section.chipText && <StatusBadge tone={CHIP_TONE[section.chip]}>{section.chipText}</StatusBadge>}{section.timing && <span className="plan-card-timing">{section.timing}</span>}</div>
          </div>
          {section.solves.length > 0 && (
            <div className="plan-solves">
              <span>What It Solves</span>
              <ul>{section.solves.map((entry) => <li key={entry}>{entry}</li>)}</ul>
            </div>
          )}
          {section.tryIt && <p className="plan-tryit">{section.tryIt}</p>}
          {section.prototype && <div className="plan-actions"><ExternalLink href={section.prototype.url}>{section.prototype.label}</ExternalLink></div>}
        </>
      )
    case 'feedback':
    case 'also':
      return section.items.length
        ? <ul className="plan-list">{section.items.map((entry) => <li key={entry.text}><span>{entry.text}</span><StatusBadge tone={CHIP_TONE[entry.chip]}>{entry.chipText}</StatusBadge></li>)}</ul>
        : <EmptyState compact icon={Inbox} title="Nothing Here Yet" description="Items show here as we add them." headingLevel={4} />
  }
}

function TaskList({ plan, link, onToggle }: { plan: ResponsePlanView; link: { label: string; url: string } | null; onToggle: (task: PlanTask) => Promise<void> }) {
  const [status, setStatus] = useState<TaskStatusFilter>('open')
  const [owner, setOwner] = useState<TaskOwnerFilter>('all')
  const [busy, setBusy] = useState<string | null>(null)
  const shown = filterTasks(plan.tasks, status, owner)
  const statusOptions: Array<[TaskStatusFilter, string]> = [['open', 'Open'], ['done', 'Done'], ['all', 'All']]
  const ownerOptions: Array<[TaskOwnerFilter, string]> = [['all', 'Everyone'], ['prentus', 'Prentus'], ['customer', plan.customerLabel]]
  async function toggle(task: PlanTask) {
    setBusy(task.id)
    await onToggle(task)
    setBusy(null)
  }
  return (
    <div className="plan-tasks">
      <div className="plan-filters">
        <div className="plan-segment" role="group" aria-label="Filter tasks by status">
          {statusOptions.map(([value, label]) => <button key={value} type="button" aria-pressed={status === value} onClick={() => setStatus(value)}>{label}</button>)}
        </div>
        <div className="plan-segment" role="group" aria-label="Filter tasks by owner">
          {ownerOptions.map(([value, label]) => <button key={value} type="button" aria-pressed={owner === value} onClick={() => setOwner(value)}>{label}</button>)}
        </div>
        <span className="plan-task-count" role="status">{shown.length} {shown.length === 1 ? 'task' : 'tasks'}</span>
      </div>
      {shown.length === 0
        ? <EmptyState compact icon={Inbox} title="No Tasks Match This Filter" description="Choose another filter to see more tasks." headingLevel={4} />
        : <ul className="plan-task-list">{shown.map((task) => <TaskRow key={task.id} task={task} customerLabel={plan.customerLabel} canEdit={plan.viewer.canReply} busy={busy === task.id} onToggle={() => void toggle(task)} />)}</ul>}
      {link && <div className="plan-actions"><ExternalLink href={link.url}>{link.label}</ExternalLink></div>}
    </div>
  )
}

function TaskRow({ task, customerLabel, canEdit, busy, onToggle }: { task: PlanTask; customerLabel: string; canEdit: boolean; busy: boolean; onToggle: () => void }) {
  const [open, setOpen] = useState(false)
  const detailId = useId()
  return (
    <li className={`plan-task${task.done ? ' is-done' : ''}`}>
      <div className="plan-task-main">
        {canEdit
          ? (
            <button type="button" className="plan-task-check" aria-pressed={task.done} aria-label={task.done ? `Open again: ${task.title}` : `Mark done: ${task.title}`} disabled={busy} onClick={onToggle}>
              {busy ? <SpinnerGap size={14} className="spin" aria-hidden="true" /> : task.done ? <Check size={14} aria-hidden="true" /> : null}
            </button>
          )
          : <span className="plan-task-check" aria-hidden="true">{task.done && <Check size={14} />}</span>}
        <button type="button" className="plan-task-title" aria-expanded={open} aria-controls={detailId} onClick={() => setOpen((value) => !value)}>
          <span>{task.title}</span>
          <ChevronDown size={16} aria-hidden="true" className={open ? 'is-open' : ''} />
        </button>
        <span className="plan-task-meta">
          <span className={`plan-owner plan-owner--${task.owner}`}>{task.owner === 'prentus' ? 'Prentus' : customerLabel}</span>
          {task.done ? <StatusBadge tone="success">Done</StatusBadge> : task.due && <StatusBadge tone="neutral">{task.due}</StatusBadge>}
        </span>
      </div>
      {open && (
        <div className="plan-task-detail" id={detailId}>
          {task.ownerName && <p className="plan-task-who">Owner: {task.ownerName}</p>}
          {task.detail && <p>{task.detail}</p>}
          {task.done && task.doneAt && <p className="plan-task-done">Marked done by {task.doneBy || 'Prentus'} on {formatThreadTime(task.doneAt)}</p>}
        </div>
      )}
      {!open && task.done && task.doneAt && <p className="plan-task-done plan-task-done--inline">Done by {task.doneBy || 'Prentus'}, {formatThreadTime(task.doneAt)}</p>}
    </li>
  )
}

function SectionNotes({ section, notes, plan, pendingNotes, onNote, onReply, onModerate }: {
  section: PlanSection
  notes: PlanNote[]
  plan: ResponsePlanView
  pendingNotes: Set<string>
  onNote: (section: PlanSection, body: string) => Promise<boolean>
  onReply: (note: PlanNote, body: string) => Promise<boolean>
  onModerate: (replyId: string, change: { body?: string; hidden?: boolean }) => Promise<boolean>
}) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const formId = useId()
  const trimmed = text.trim()

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    if (!trimmed) { setError('Write a note before you send it.'); return }
    if (trimmed.length > PLAN_NOTE_MAX_CHARS) { setError(`Keep the note under ${PLAN_NOTE_MAX_CHARS.toLocaleString('en-US')} characters.`); return }
    setError('')
    setBusy(true)
    const sent = await onNote(section, trimmed)
    setBusy(false)
    if (sent) { setText(''); setOpen(false) }
  }

  return (
    <div className="plan-notes">
      {plan.viewer.canNote && (
        <div className="plan-actions">
          <button type="button" className={`plan-note-button${notes.length ? ' has-notes' : ''}`} aria-expanded={open} aria-controls={formId} onClick={() => setOpen((value) => !value)}>
            <ChatAdd size={16} aria-hidden="true" />{open ? 'Hide Note' : 'Add a Note'}
          </button>
        </div>
      )}
      {open && plan.viewer.canNote && (
        <form id={formId} className="plan-note-form" onSubmit={(event) => void submit(event)} noValidate>
          <Field label={`Your Note on ${section.title}`} error={error || undefined} hint={`${trimmed.length.toLocaleString('en-US')} of ${PLAN_NOTE_MAX_CHARS.toLocaleString('en-US')} characters. Your team and Prentus see it.${plan.assistantEnabled ? ' The Prentus Assistant answers questions right away.' : ''}`}>
            <textarea value={text} rows={4} maxLength={PLAN_NOTE_MAX_CHARS} autoFocus onChange={(event) => setText(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) void submit(event) }} />
          </Field>
          <div className="thread-form-actions">
            <Button type="submit" compact disabled={busy}>{busy && <SpinnerGap size={14} className="spin" aria-hidden="true" />}Send Note</Button>
            <Button type="button" variant="secondary" compact disabled={busy} onClick={() => { setOpen(false); setError('') }}>Cancel</Button>
          </div>
        </form>
      )}
      {notes.length > 0 && (
        <ol className="plan-note-list" aria-label={`Notes on ${section.title}`}>
          {notes.map((note) => (
            <li key={note.id} className="plan-note">
              <p className="plan-note-head"><strong>{note.authorName}</strong> <time dateTime={note.createdAt}>{formatThreadTime(note.createdAt)}</time></p>
              <p className="plan-note-body">{note.body}</p>
              <ThreadReplies
                replies={note.replies}
                label={`${note.authorName}'s note on ${section.title}`}
                canReply={plan.viewer.canReply}
                canModerate={plan.viewer.canModerate}
                assistantPending={pendingNotes.has(note.id)}
                onReply={(body) => onReply(note, body)}
                onModerate={onModerate}
              />
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
